import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Copy, Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useApiError } from '@/hooks/useApiError'
import {
  downloadPartnerStatement,
  useBecomePartner,
  useChangePayout,
  useDeclareOpportunity,
  usePartnerApi,
  usePartnerSpace,
  useRequestPayoutCode,
  type PartnerClient,
  type PartnerProfile,
  type PartnerProgram,
} from '@/api/partenaires'

/**
 * The partner space, laid out like a partner program's dashboard: the link to
 * copy beside the title, the numbers first (always shown, even at zero), the
 * clients and the payments in two tabs, and on the side what the partner
 * earns, where the money goes and the monthly statement. Before joining, the
 * same page explains how it works in three steps, with the agreement to accept.
 * Shown in Settings for a client, and as is on the public partners' address.
 * A partner only sees their own data.
 */

const SERVER_CODES: Record<string, string> = {
  ferme: 'partner.errors.closed',
  entente_changee: 'partner.errors.agreementChanged',
  pas_client: 'partner.errors.notClient',
  nom_manquant: 'partner.errors.nameMissing',
  trop: 'partner.errors.tooMany',
}

function useFormat() {
  const { i18n } = useTranslation()
  const locale = i18n.language?.startsWith('en') ? 'en-CA' : 'fr-CA'
  return {
    // "500 $" for a round amount, "28,34 $" otherwise.
    money: (amount: string) => {
      const n = Number(amount)
      return n.toLocaleString(locale, { style: 'currency', currency: 'CAD', minimumFractionDigits: Number.isInteger(n) ? 0 : 2 })
    },
    day: (iso: string) => new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
    date: (iso: string) => new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
    month: (ym: string) => {
      const [y, m] = ym.split('-').map(Number)
      return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' })
    },
  }
}

function useServerError() {
  const { t } = useTranslation()
  const { handleError, extractMessage } = useApiError()
  return (err: unknown) => {
    const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? ''
    if (SERVER_CODES[code]) toast.error(t(SERVER_CODES[code], extractMessage(err)))
    else handleError(err)
  }
}

async function copy(value: string, done: string, failed: string) {
  try {
    await navigator.clipboard.writeText(value)
    toast.success(done)
  } catch {
    toast.error(failed)
  }
}

const CARD = 'rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'

/** The page title, with what sits on its right (the link to share). */
function Header({ title, subtitle, children }: { title: string; subtitle: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{title}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
      </div>
      {children}
    </div>
  )
}

/** What the partner earns, as a short scale on the side, like a program's reward rules. */
function Rules({ programme, partner, clientOffer = true }: { programme: PartnerProgram; partner?: PartnerProfile; clientOffer?: boolean }) {
  const { t } = useTranslation()
  const { money } = useFormat()
  const c = partner?.commissions
  const rows = c
    ? [{ label: t('partner.rules.share', 'Sur chaque facture payée'), value: '20 %' },
        { label: t('partner.rules.during', 'Pendant'), value: t('partner.rules.months', '12 mois') },
        { label: t('partner.rules.payout', 'Versé chaque mois dès'), value: money(c.seuil) }]
    : programme.bareme.map((b) => ({ label: t('partner.rules.client', 'Client {{plan}}', { plan: b.forfait }), value: money(b.montant) }))
  return (
    <div className={cn(CARD, 'px-5 py-4')}>
      <h2 className="text-sm font-semibold text-slate-900 dark:text-white">{t('partner.rules.title', 'Ce que vous gagnez')}</h2>
      <dl className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
        {rows.map((r) => (
          <div key={r.label} className="flex justify-between gap-3 py-2 text-sm">
            <dt className="text-slate-600 dark:text-slate-300">{r.label}</dt>
            <dd className="font-semibold tabular-nums text-slate-900 dark:text-white">{r.value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-2 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
        {c ? t('partner.rules.notePublic', 'Factures payées seulement, pendant les 12 premiers mois du client.')
          : t('partner.rules.note', 'Par client qui devient payant, en trois fois avec ses factures payées. Bonus IA au 13e mois.')}
      </p>
      {clientOffer && (
        <p className="mt-2 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
          {t('partner.offer.them', 'Votre client a son deuxième mois offert, jusqu\'à {{cap}}', { cap: money(programme.credit_plafond) })}
        </p>
      )}
    </div>
  )
}

/** The agreement in the partner's language: both versions are equally binding (section 15.6). */
function useAgreement(programme: PartnerProgram) {
  const { i18n } = useTranslation()
  const langue: 'fr' | 'en' = i18n.language?.startsWith('en') ? 'en' : 'fr'
  return { langue, url: langue === 'en' && programme.entente_url_en ? programme.entente_url_en : programme.entente_url }
}

/** Before joining: how it works in three steps, the scale on the side, the agreement to accept. */
function Join({ programme }: { programme: PartnerProgram }) {
  const { t } = useTranslation()
  const agreement = useAgreement(programme)
  const { money } = useFormat()
  const onError = useServerError()
  const become = useBecomePartner()
  const [accepted, setAccepted] = useState(false)
  const steps = [
    [t('partner.join.step1', 'Vous partagez votre lien'),
      t('partner.join.step1Detail', 'Par courriel, texto ou de vive voix. Votre code marche aussi, si la personne s\'inscrit sans le lien.')],
    [t('partner.join.step2', 'Votre client s\'inscrit et paie'),
      t('partner.join.step2Detail', 'Il a son deuxième mois offert, jusqu\'à {{cap}}.', { cap: money(programme.credit_plafond) })],
    [t('partner.join.step3', 'Vous recevez votre prime'),
      t('partner.join.step3Detail', 'En trois fois, avec ses factures payées, par Interac ou PayPal. Vous suivez tout ici : visites, inscriptions, clients payants.')],
  ]

  return (
    <>
      <Header title={t('partner.join.title', 'Programme partenaires')}
        subtitle={t('partner.join.subtitle', 'Recommandez Callizy aux entreprises que vous connaissez. Vous êtes payé quand elles deviennent clientes.')} />
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        <section className={CARD}>
          <h2 className="border-b border-slate-100 px-5 py-3.5 text-sm font-semibold text-slate-900 dark:border-slate-800 dark:text-white">
            {t('partner.join.how', 'Comment ça marche')}
          </h2>
          <ol className="divide-y divide-slate-100 px-5 dark:divide-slate-800">
            {steps.map(([title, detail], i) => (
              <li key={title} className="flex gap-3.5 py-4">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 text-xs font-semibold text-slate-900 dark:border-slate-700 dark:text-white">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{title}</p>
                  <p className="mt-0.5 text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">{detail}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-b-xl border-t border-slate-100 bg-slate-50 px-5 py-3.5 dark:border-slate-800 dark:bg-slate-900/60">
            <label className="flex items-center gap-2.5 text-sm text-slate-600 dark:text-slate-400">
              <input type="checkbox" className="h-4 w-4 accent-slate-900 dark:accent-white" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
              <span>
                {t('partner.join.accept', 'J\'accepte l\'entente de partenariat.')}{' '}
                <a href={agreement.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline dark:text-blue-400">
                  {t('partner.join.read', 'Lire l\'entente')}
                </a>
              </span>
            </label>
            <Button type="button" disabled={!accepted || become.isPending}
              onClick={() => become.mutate({ version: programme.entente_version, langue: agreement.langue }, { onError })}>
              {become.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('partner.join.button', 'Devenir partenaire')}
            </Button>
          </div>
        </section>
        <Rules programme={programme} clientOffer={false} />
      </div>
    </>
  )
}

/** The numbers first, always shown, even at zero: visits, sign-ups, paying clients, money. */
function Stats({ partner }: { partner: PartnerProfile }) {
  const { t } = useTranslation()
  const { money } = useFormat()
  const n = partner.compteurs
  const c = partner.commissions
  const items = [
    { label: t('partner.stats.clicks', 'Visites'), value: String(n.clics), note: t('partner.stats.clicksMonth', '{{n}} ce mois', { n: n.clics_ce_mois }) },
    { label: t('partner.stats.signups', 'Inscriptions'), value: String(n.inscriptions), note: t('partner.stats.trials', '{{n}} en essai', { n: n.essais }) },
    { label: t('partner.stats.paying', 'Clients payants'), value: String(n.payants), note: t('partner.stats.payingOf', 'sur {{n}} inscrits', { n: n.inscriptions, count: n.inscriptions }) },
    { label: t('partner.stats.owed', 'À recevoir'), value: money(c ? c.a_recevoir : partner.primes.a_recevoir),
      note: c ? t('partner.stats.threshold', 'versé dès {{amount}}', { amount: money(c.seuil) })
        : t('partner.stats.upcoming', '{{amount}} à venir', { amount: money(partner.primes.a_venir) }) },
    { label: t('partner.stats.received', 'Déjà reçu'), value: money(c ? c.recues : partner.primes.recues), note: t('partner.stats.sinceStart', 'depuis le début') },
  ]
  return (
    // Hairlines between the cells: the grid's own background shows through a 1px gap.
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 dark:border-slate-800 dark:bg-slate-800 md:grid-cols-5">
      {items.map((item, i) => (
        <div key={item.label} className={cn('bg-white px-4 py-3.5 dark:bg-slate-900', i === items.length - 1 && 'col-span-2 md:col-span-1')}>
          <p className="text-xs text-slate-500 dark:text-slate-400">{item.label}</p>
          <p className="mt-1 text-[22px] font-semibold leading-tight tabular-nums text-slate-900 dark:text-white">{item.value}</p>
          <p className="mt-0.5 truncate text-[11.5px] text-slate-400 dark:text-slate-500">{item.note}</p>
        </div>
      ))}
    </div>
  )
}

const STATE_TONES: Record<PartnerClient['etat'], string> = {
  inscrit: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  essai: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
  payant: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  annule: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
}

/** The bonus column of the client track: "Team · 85 $", then where it stands. */
function bonusCell(c: PartnerClient, t: TFunction, money: (a: string) => string, date: (iso: string) => string) {
  if (!c.prime) return { main: '', notes: [t('partner.clients.noPlan', 'Pas encore d\'abonnement')], earned: 0 }
  const earned = c.prime.mensualites.filter((m) => m.etat === 'a_verser' || m.etat === 'verse').length
  const notes = [c.prime.etat === 'annulee'
    ? t('partner.clients.bonusCancelled', 'annulée')
    : earned >= 3 ? t('partner.clients.bonusComplete', 'gagnée en entier')
      : earned === 0 ? t('partner.clients.bonusFirst', 'dès sa première facture payée')
        : t('partner.clients.bonusProgress', '{{n}} versement sur 3', { n: earned, count: earned })]
  if (c.bonus_ia && c.bonus_ia.etat === 'estime') {
    notes.push(t('partner.clients.aiEstimated', 'bonus IA {{amount}} à ce jour, dû le {{date}}', { amount: money(c.bonus_ia.montant), date: date(c.bonus_ia.du_le) }))
  }
  return { main: `${c.prime.forfait} · ${money(c.prime.montant)}`, notes, earned: c.prime.etat === 'annulee' ? 0 : earned }
}

function Clients({ partner }: { partner: PartnerProfile }) {
  const { t } = useTranslation()
  const { money, day, date } = useFormat()
  const clientTrack = partner.voie === 'clients'
  const STATES: Record<PartnerClient['etat'], string> = {
    inscrit: t('partner.clients.signedUp', 'Inscrit'),
    essai: t('partner.clients.trial', 'En essai'),
    payant: t('partner.clients.paying', 'Payant'),
    annule: t('partner.clients.cancelled', 'Parti'),
  }
  if (!partner.clients.length) {
    return (
      <p className="px-5 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
        {t('partner.clients.empty', 'Personne encore. Partagez votre lien : chaque client payant vous rapporte.')}
      </p>
    )
  }
  const th = 'px-3 py-2.5 text-left sm:px-5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500'
  return (
    <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="border-b border-slate-100 dark:border-slate-800">
        <tr>
          <th className={th}>{t('partner.clients.company', 'Entreprise')}</th>
          <th className={th}>{t('partner.clients.state', 'État')}</th>
          {clientTrack && <th className={cn(th, 'hidden sm:table-cell')}>{t('partner.clients.bonus', 'Prime')}</th>}
          <th className={cn(th, 'text-right')}>{t('partner.clients.earned', 'Gagné')}</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
        {partner.clients.map((c) => {
          const bonus = clientTrack ? bonusCell(c, t, money, date) : null
          return (
            <tr key={`${c.entreprise}-${c.le}`}>
              <td className="px-3 py-3 sm:px-5">
                <p className="max-w-[8rem] truncate font-medium sm:max-w-[13rem] text-slate-900 dark:text-white">{c.entreprise}</p>
                <p className="whitespace-nowrap text-xs text-slate-400 dark:text-slate-500">{t('partner.clients.since', 'Depuis le {{date}}', { date: day(c.le) })}</p>
              </td>
              <td className="px-3 py-3 sm:px-5">
                <span className={cn('whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium', STATE_TONES[c.etat])}>{STATES[c.etat]}</span>
              </td>
              {bonus && (
                <td className="hidden px-5 py-3 sm:table-cell">
                  {bonus.main && <p className="whitespace-nowrap text-slate-900 dark:text-white">{bonus.main}</p>}
                  {bonus.notes.map((note) => <p key={note} className="text-xs text-slate-400 dark:text-slate-500">{note}</p>)}
                </td>
              )}
              <td className="px-3 py-3 text-right sm:px-5">
                <p className={cn('font-semibold tabular-nums', Number(c.gagne) > 0 ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500')}>
                  {money(c.gagne)}
                </p>
                {bonus && bonus.main && (
                  <div className="ml-auto mt-1.5 h-1 w-14 overflow-hidden sm:w-20 rounded-full bg-slate-100 dark:bg-slate-800"
                    role="img" aria-label={t('partner.clients.bonusProgress', '{{n}} versement sur 3', { n: bonus.earned, count: bonus.earned })}>
                    <div className="h-full rounded-full bg-slate-900 dark:bg-white" style={{ width: `${(bonus.earned / 3) * 100}%` }} />
                  </div>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
    </div>
  )
}

/** Every payment, one line each: an instalment of a bonus, an AI bonus, or a commission (public track). */
function Earnings({ partner }: { partner: PartnerProfile }) {
  const { t } = useTranslation()
  const { money, day } = useFormat()
  const c = partner.commissions
  const label: Record<string, string> = {
    attente: t('partner.earnings.upcoming', 'À venir'),
    estime: t('partner.earnings.estimated', 'Estimé'),
    a_verser: t('partner.earnings.owed', 'À recevoir'),
    verse: t('partner.earnings.paid', 'Reçu'),
    versee: t('partner.earnings.paid', 'Reçu'),
    annule: t('partner.earnings.cancelled', 'Annulé'),
    annulee: t('partner.earnings.cancelled', 'Annulé'),
    retiree: t('partner.earnings.refunded', 'Retiré, facture remboursée'),
  }
  const rows = c
    ? c.detail.map((d) => ({ who: d.entreprise, what: day(d.le), amount: d.montant, state: d.etat }))
    : partner.clients.flatMap((cl) => [
        ...(cl.prime?.mensualites ?? []).map((m) => ({
          who: cl.entreprise, what: t('partner.earnings.instalment', 'Prime, versement {{n}} sur 3', { n: m.rang }), amount: m.montant, state: m.etat,
        })),
        ...(cl.bonus_ia && cl.bonus_ia.etat !== 'rien'
          ? [{ who: cl.entreprise, what: t('partner.earnings.aiBonus', 'Bonus IA, dû le {{date}}', { date: day(cl.bonus_ia.du_le) }),
              amount: cl.bonus_ia.montant, state: cl.bonus_ia.etat }]
          : []),
      ])
  const crossed = (state: string) => ['annule', 'annulee', 'retiree'].includes(state)

  return (
    <div className="pb-1">
      {c?.sous_seuil && (
        <p className="border-b border-slate-100 px-5 py-2.5 text-[13px] text-slate-500 dark:border-slate-800 dark:text-slate-400">
          {t('partner.earnings.underThreshold', 'Versé chaque mois dès {{threshold}} : le vôtre attend le mois prochain.', { threshold: money(c.seuil) })}
        </p>
      )}
      {rows.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
          {t('partner.earnings.empty', 'Rien encore. Vos gains s\'inscrivent ici dès la première facture payée d\'un client.')}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
          {rows.slice(0, 60).map((r, i) => (
            <li key={`${r.who}-${r.what}-${i}`} className="flex items-center gap-3 px-5 py-2.5">
              <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-300">
                <span className="font-medium text-slate-900 dark:text-white">{r.who}</span> · {r.what}
              </span>
              <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">{label[r.state] ?? ''}</span>
              <span className={cn('w-20 shrink-0 text-right tabular-nums', crossed(r.state) ? 'text-slate-400 line-through' : 'text-slate-900 dark:text-white')}>
                {money(r.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Where the money goes, and the statement of a month. */
function PayoutCard({ partner, onPayout }: { partner: PartnerProfile; onPayout: () => void }) {
  const { t } = useTranslation()
  const { month } = useFormat()
  const http = usePartnerApi()
  const { handleError } = useApiError()
  const [statement, setStatement] = useState(partner.releves[0] ?? '')
  const [downloading, setDownloading] = useState(false)
  const payout = partner.versement.adresse
    ? `${partner.versement.mode === 'paypal' ? 'PayPal' : 'Interac'} · ${partner.versement.adresse}`
    : t('partner.payout.none', 'Pas encore donné')

  const download = async () => {
    setDownloading(true)
    try {
      await downloadPartnerStatement(http, statement, partner.code)
    } catch (err) {
      handleError(err)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className={cn(CARD, 'divide-y divide-slate-100 px-5 dark:divide-slate-800')}>
      <div className="flex items-center justify-between gap-3 py-3.5">
        <div className="min-w-0">
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('partner.payout.label', 'Versement')}</p>
          <p className="truncate text-sm font-medium text-slate-900 dark:text-white">{payout}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onPayout}>
          {partner.versement.adresse ? t('partner.payout.change', 'Modifier') : t('partner.payout.add', 'Ajouter')}
        </Button>
      </div>
      {partner.releves.length > 0 && (
        <div className="flex items-center justify-between gap-3 py-3.5">
          <div className="min-w-0">
            <p className="text-xs text-slate-500 dark:text-slate-400">{t('partner.earnings.statement', 'Relevé du mois')}</p>
            <select aria-label={t('partner.earnings.statementMonth', 'Mois du relevé')} value={statement} onChange={(e) => setStatement(e.target.value)}
              className="-ml-0.5 bg-transparent pr-1 text-sm font-medium text-slate-900 focus:outline-none dark:text-white">
              {partner.releves.map((m) => <option key={m} value={m}>{month(m).replace(/^./, (ch) => ch.toUpperCase())}</option>)}
            </select>
          </div>
          <Button type="button" variant="outline" size="sm" disabled={!statement || downloading} onClick={() => void download()}>
            {downloading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}
            {t('partner.earnings.download', 'Télécharger')}
          </Button>
        </div>
      )}
    </div>
  )
}

/** Where the payments go. Changing it needs a fresh code sent to the account's email. */
function PayoutDialog({ partner, open, onClose }: { partner: PartnerProfile; open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const { handleError } = useApiError()
  const requestCode = useRequestPayoutCode()
  const change = useChangePayout()
  const [form, setForm] = useState({ mode: partner.versement.mode || 'interac', adresse: partner.versement.adresse, code: '' })
  const [codeSent, setCodeSent] = useState(false)
  const close = () => { setCodeSent(false); setForm((f) => ({ ...f, code: '' })); onClose() }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('partner.payout.title', 'Où vous verser')}</DialogTitle>
          <DialogDescription>{t('partner.payout.why', 'Un code part à votre courriel pour confirmer. Un avis suit le changement.')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {(['interac', 'paypal'] as const).map((mode) => (
              <button key={mode} type="button" onClick={() => setForm({ ...form, mode })}
                className={cn('h-10 rounded-md border text-sm font-medium transition-colors',
                  form.mode === mode ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
                    : 'border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300')}>
                {mode === 'interac' ? t('partner.payout.interac', 'Interac (Canada)') : 'PayPal'}
              </button>
            ))}
          </div>
          <Input type="email" placeholder={t('partner.payout.address', 'Courriel Interac ou PayPal')} value={form.adresse}
            onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
          {codeSent && (
            <Input inputMode="numeric" maxLength={6} placeholder={t('partner.payout.code', 'Code reçu par courriel')} value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.replace(/\D/g, '') })} />
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={close}>{t('common.cancel', 'Annuler')}</Button>
          {!codeSent ? (
            <Button type="button" className="bg-blue-600 text-white hover:bg-blue-700" disabled={!form.adresse.includes('@') || requestCode.isPending}
              onClick={() => requestCode.mutate(undefined, { onSuccess: () => setCodeSent(true), onError: handleError })}>
              {t('partner.payout.sendCode', 'Recevoir un code')}
            </Button>
          ) : (
            <Button type="button" className="bg-blue-600 text-white hover:bg-blue-700" disabled={form.code.length < 6 || change.isPending}
              onClick={() => change.mutate(form, {
                onSuccess: () => { toast.success(t('partner.payout.saved', 'C\'est changé. Un avis vous est envoyé.')); close() },
                onError: handleError,
              })}>
              {t('partner.payout.save', 'Enregistrer')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeclareDialog({ partner, open, onClose }: { partner: PartnerProfile; open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const { day } = useFormat()
  const onError = useServerError()
  const declare = useDeclareOpportunity()
  const [form, setForm] = useState({ entreprise: '', contact: '', note: '' })
  const STATES: Record<string, string> = {
    en_attente: t('partner.declare.pending', 'En vérification'),
    rattachee: t('partner.declare.linked', 'Rattachée'),
    refusee: t('partner.declare.declined', 'Pas retenue'),
  }
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('partner.declare.title', 'Recommandé sans votre lien ?')}</DialogTitle>
          <DialogDescription>{t('partner.declare.help', 'Donnez le nom de l\'entreprise. Nous vérifions, puis la rattachons à votre compte.')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5">
          <Input placeholder={t('partner.declare.company', 'Nom de l\'entreprise')} value={form.entreprise} maxLength={150}
            onChange={(e) => setForm({ ...form, entreprise: e.target.value })} />
          <Input placeholder={t('partner.declare.contact', 'Courriel ou téléphone (facultatif)')} value={form.contact} maxLength={150}
            onChange={(e) => setForm({ ...form, contact: e.target.value })} />
          <Textarea rows={2} placeholder={t('partner.declare.note', 'Note (facultatif)')} value={form.note} maxLength={500}
            onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </div>
        {partner.occasions.length > 0 && (
          <ul className="max-h-32 divide-y divide-slate-100 overflow-y-auto text-sm dark:divide-slate-800">
            {partner.occasions.map((o) => (
              <li key={o.id} className="flex justify-between gap-3 py-1.5">
                <span className="min-w-0 truncate text-slate-700 dark:text-slate-300">{o.entreprise}</span>
                <span className="shrink-0 text-slate-500 dark:text-slate-400">{STATES[o.statut]} · {day(o.le)}</span>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>{t('common.cancel', 'Annuler')}</Button>
          <Button type="button" className="bg-blue-600 text-white hover:bg-blue-700" disabled={form.entreprise.trim().length < 2 || declare.isPending}
            onClick={() => declare.mutate(form, {
              onSuccess: () => { setForm({ entreprise: '', contact: '', note: '' }); toast.success(t('partner.declare.sent', 'Merci, nous vérifions.')) },
              onError,
            })}>
            {t('partner.declare.button', 'Envoyer')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Space({ partner, programme }: { partner: PartnerProfile; programme: PartnerProgram }) {
  const { t } = useTranslation()
  const { money } = useFormat()
  const onError = useServerError()
  const become = useBecomePartner()
  const agreement = useAgreement(programme)
  const publicTrack = partner.voie === 'publique'
  const [tab, setTab] = useState<'clients' | 'gains'>('clients')
  const [payoutOpen, setPayoutOpen] = useState(false)
  const [declareOpen, setDeclareOpen] = useState(false)
  const copied = t('partner.copied', 'Copié')
  const failed = t('partner.copyFailed', 'La copie a échoué. Sélectionnez le texte à la main.')
  const message = publicTrack
    ? t('partner.link.messagePublic', 'Je recommande Callizy pour les appels de votre entreprise. Avec mon lien, votre deuxième mois est offert (jusqu\'à {{cap}}) : {{link}}',
      { cap: money(programme.credit_plafond), link: partner.lien })
    : t('partner.link.message', 'J\'utilise Callizy pour les appels de mon entreprise. Avec mon lien, ton deuxième mois est offert (jusqu\'à {{cap}}) : {{link}}',
      { cap: money(programme.credit_plafond), link: partner.lien })

  return (
    <>
      <Header title={t('partner.title', 'Espace partenaire')}
        subtitle={t('partner.link.code', 'Votre code : {{code}}, à donner si la personne s\'inscrit sans le lien', { code: partner.code })}>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <div className="flex h-9 min-w-0 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white">
            <span className="truncate">{partner.lien.replace(/^https?:\/\//, '')}</span>
          </div>
          <Button type="button" className="h-9" onClick={() => void copy(partner.lien, copied, failed)}>
            <Copy className="mr-2 h-4 w-4" />{t('partner.link.copy', 'Copier le lien')}
          </Button>
          <Button type="button" variant="outline" className="h-9" onClick={() => void copy(message, copied, failed)}>
            {t('partner.link.copyMessage', 'Copier un message')}
          </Button>
        </div>
      </Header>

      {!partner.actif && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          {t('partner.paused', 'Votre lien est en pause. Écrivez-nous à support@callizy.ca.')}
        </p>
      )}
      {partner.entente_a_accepter && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-900 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-100">
          <span className="min-w-0 flex-1">
            {t('partner.agreement.text', 'Acceptez l\'entente pour recevoir vos versements.')}{' '}
            <a href={agreement.url} target="_blank" rel="noreferrer" className="underline">{t('partner.join.read', 'Lire l\'entente')}</a>
          </span>
          <Button type="button" size="sm" disabled={become.isPending}
            onClick={() => become.mutate({ version: programme.entente_version, langue: agreement.langue }, { onError })}>
            {t('partner.agreement.accept', 'J\'accepte')}
          </Button>
        </div>
      )}

      <Stats partner={partner} />

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        <section className={CARD}>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-slate-100 px-5 dark:border-slate-800">
            <div role="tablist" className="flex gap-6">
              {([['clients', t('partner.tabs.clients', 'Clients')], ['gains', publicTrack ? t('partner.tabs.commissions', 'Commissions') : t('partner.tabs.bonuses', 'Primes')]] as const).map(([id, label]) => (
                <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
                  className={cn('-mb-px border-b-2 py-3.5 text-sm font-medium transition-colors',
                    tab === id ? 'border-slate-900 text-slate-900 dark:border-white dark:text-white'
                      : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200')}>
                  {label}{id === 'clients' && partner.clients.length > 0 ? ` ${partner.clients.length}` : ''}
                </button>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" className="my-2" onClick={() => setDeclareOpen(true)}>
              {partner.occasions.length
                ? t('partner.declare.openCount', 'Sans le lien ({{n}})', { n: partner.occasions.length })
                : t('partner.declare.open', 'Déclarer un client sans le lien')}
            </Button>
          </div>
          {tab === 'clients' ? <Clients partner={partner} /> : <Earnings partner={partner} />}
        </section>

        <aside className="space-y-4">
          <Rules programme={programme} partner={partner} />
          <PayoutCard partner={partner} onPayout={() => setPayoutOpen(true)} />
        </aside>
      </div>

      <PayoutDialog partner={partner} open={payoutOpen} onClose={() => setPayoutOpen(false)} />
      <DeclareDialog partner={partner} open={declareOpen} onClose={() => setDeclareOpen(false)} />
    </>
  )
}

export function PartnerSpace() {
  const { t } = useTranslation()
  const query = usePartnerSpace()
  const data = query.data

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-6xl space-y-5 px-4 py-6 sm:px-6 lg:px-8">
        {query.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        ) : query.isError || !data ? (
          <p className="text-sm text-rose-600">{t('partner.loadError', 'L\'espace ne répond pas. Rechargez la page.')}</p>
        ) : data.partenaire ? (
          <Space partner={data.partenaire} programme={data.programme} />
        ) : (
          <Join programme={data.programme} />
        )}
      </div>
    </div>
  )
}
